import { randomLetter } from './words';

export type Grid = (string | null)[];
export type Placed = Map<number, string>;
export type Verdict =
  | { ok: true; words: string[] }
  | { ok: false; reason: 'empty' | 'line' | 'gap' | 'short' | 'join' | 'word'; word?: string };

export const RACK_SIZE = 7;

function letterAt(grid: Grid, placed: Placed, i: number): string | null {
  return placed.get(i) ?? grid[i];
}

function run(grid: Grid, placed: Placed, n: number, idx: number, horizontal: boolean): number[] {
  const step = horizontal ? 1 : n;
  const sameLine = (a: number, b: number) => (horizontal ? Math.floor(a / n) === Math.floor(b / n) : true);
  let start = idx;
  while (start - step >= 0 && sameLine(start - step, idx) && letterAt(grid, placed, start - step)) start -= step;
  const cells: number[] = [];
  for (let i = start; i < n * n && sameLine(i, idx) && letterAt(grid, placed, i); i += step) cells.push(i);
  return cells;
}

const wordOf = (grid: Grid, placed: Placed, cells: number[]) => cells.map((i) => letterAt(grid, placed, i)).join('');

export function check(grid: Grid, placed: Placed, n: number, valid: (w: string) => boolean): Verdict {
  const cells = [...placed.keys()].sort((a, b) => a - b);
  if (cells.length === 0) return { ok: false, reason: 'empty' };
  const rows = new Set(cells.map((i) => Math.floor(i / n)));
  const cols = new Set(cells.map((i) => i % n));
  if (rows.size > 1 && cols.size > 1) return { ok: false, reason: 'line' };
  const firstTurn = grid.every((c) => c === null);

  const runs: number[][] = [];
  if (cells.length === 1) {
    for (const h of [true, false]) { const r = run(grid, placed, n, cells[0], h); if (r.length >= 2) runs.push(r); }
    if (runs.length === 0) return { ok: false, reason: 'short' };
  } else {
    const horizontal = rows.size === 1;
    const step = horizontal ? 1 : n;
    for (let i = cells[0]; i <= cells[cells.length - 1]; i += step) {
      if (!letterAt(grid, placed, i)) return { ok: false, reason: 'gap' };
    }
    runs.push(run(grid, placed, n, cells[0], horizontal));
    for (const c of cells) { const r = run(grid, placed, n, c, !horizontal); if (r.length >= 2) runs.push(r); }
  }

  if (!firstTurn) {
    const touches = runs.some((r) => r.some((i) => !placed.has(i)));
    if (!touches) return { ok: false, reason: 'join' };
  }

  const words = runs.map((r) => wordOf(grid, placed, r));
  for (const w of words) if (!valid(w)) return { ok: false, reason: 'word', word: w };
  return { ok: true, words };
}

export type Plan = { word: string; needed: string[] };

export function findPlan(grid: Grid, n: number, words: string[], valid: (w: string) => boolean): Plan | null {
  const pool = [...words].filter((w) => w.length <= n).sort(() => Math.random() - 0.5);
  const empty = grid.every((c) => c === null);
  if (empty) {
    const w = pool.find((x) => x.length >= 2);
    return w ? { word: w, needed: w.split('') } : null;
  }
  const letters = grid.map((c, i) => [c, i] as const).filter(([c]) => c !== null).sort(() => Math.random() - 0.5);
  for (const word of pool) {
    for (const [letter, at] of letters) {
      for (let k = 0; k < word.length; k++) {
        if (word[k] !== letter) continue;
        for (const horizontal of [true, false]) {
          const step = horizontal ? 1 : n;
          const r = Math.floor(at / n), c = at % n;
          const start = horizontal ? at - k : at - k * n;
          const endRow = horizontal ? r : r - k + word.length - 1;
          const endCol = horizontal ? c - k + word.length - 1 : c;
          if ((horizontal ? c - k : r - k) < 0 || endRow >= n || endCol >= n) continue;
          const placed: Placed = new Map();
          let clash = false;
          for (let j = 0; j < word.length; j++) {
            const i = start + j * step;
            if (grid[i] === null) placed.set(i, word[j]);
            else if (grid[i] !== word[j]) { clash = true; break; }
          }
          if (clash || placed.size === 0) continue;
          const v = check(grid, placed, n, (w) => w === word || valid(w));
          if (v.ok && v.words[0] === word) return { word, needed: [...placed.values()] };
        }
      }
    }
  }
  return null;
}

export function refill(rack: string[], plan: Plan | null): string[] {
  const out = [...rack];
  if (plan) {
    const have = [...out];
    const missing: string[] = [];
    for (const l of plan.needed) {
      const at = have.indexOf(l);
      if (at >= 0) have.splice(at, 1); else missing.push(l);
    }
    const keep = new Set<number>();
    const pending = [...plan.needed];
    out.forEach((l, i) => { const j = pending.indexOf(l); if (j >= 0) { pending.splice(j, 1); keep.add(i); } });
    while (out.length + missing.length > RACK_SIZE) {
      const drop = out.findIndex((_, i) => !keep.has(i));
      if (drop < 0) break;
      out.splice(drop, 1);
      const shifted = new Set<number>();
      keep.forEach((k) => shifted.add(k > drop ? k - 1 : k));
      keep.clear();
      shifted.forEach((k) => keep.add(k));
    }
    out.push(...missing);
  }
  while (out.length < RACK_SIZE) out.push(randomLetter());
  return out.sort(() => Math.random() - 0.5);
}
